import type { InsurancePayment } from "@/models/simulation/subtypes";

/**
 * This composable is used to read the monthly insurance lists of an offer.
 * `monthlyFees[type]` is a list, because the fee may change over time (e.g. drops after the fixed rate period) - each entry has its own period.
 * Shared by the comparator (MonthlyCostsRow) and the printout (PrintOffersTableV25), so both show the same months and amounts.
 */
export const useCalculatorInsurances = () => {
  /** A 0 PLN fee (or one without a period) is not billed at all - it would only add an empty row / cell. */
  const isPaidInsurance = (insurance: Nullable<InsurancePayment>): boolean => !!insurance?.period && insurance.fee > 0;

  /**
   * Position of the insurance billed in the given month, or -1 when none is.
   * The index (not the entry) is returned, because `offer.fxValues[type].monthlyFees` is a parallel list - same order, FX amounts.
   */
  const getInsuranceIndexForMonth = (insurances: InsurancePayment[], month: number): number => insurances
    .findIndex((insurance) => isPaidInsurance(insurance) && month >= insurance.period.from && month <= insurance.period.until);

  /** Months in which the cost changes: every period start, and the month right after every period end (skipped when past the loan period). */
  const getInsuranceBoundaryMonths = (insurances: InsurancePayment[], loanPeriod: number | undefined): number[] =>
    [...new Set(insurances.filter(isPaidInsurance).flatMap(({ period: { from, until } }) => {
      const afterUntil: number = until + 1;
      const isAfterUntilWithinLoanPeriod: boolean = loanPeriod ? afterUntil <= loanPeriod : false;
      return (isAfterUntilWithinLoanPeriod ? [from, afterUntil] : [from])
        .filter((month) => loanPeriod === undefined || month <= loanPeriod);
    }))];

  return { isPaidInsurance, getInsuranceIndexForMonth, getInsuranceBoundaryMonths };
};
